import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { AuthService } from '../services/auth';

/**
 * Functional HTTP interceptor that attaches the JWT access token
 * to all outgoing API requests as a Bearer token.
 *
 * Reads the token from localStorage (set by AuthService on login/register).
 * Skips adding the header if no token is present (e.g. login/register requests).
 *
 * A 401 means the session is gone (the token expired or the user was removed), and
 * without this every page reported it as "Is the backend running?" (§0cr). It signs
 * out and sends the user to login, back to where they were afterwards. `/auth/`
 * requests are left alone: a 401 there is a wrong password, shown on the form.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const token = localStorage.getItem('accessToken');
  const authReq = token
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;

  const auth = inject(AuthService);
  const router = inject(Router);

  return next(authReq).pipe(
    catchError((error: unknown) => {
      if (
        error instanceof HttpErrorResponse &&
        error.status === 401 &&
        !req.url.includes('/auth/') &&
        !router.url.startsWith('/login')
      ) {
        auth.logout();
        router.navigate(['/login'], {
          queryParams: { reason: 'expired', returnUrl: router.url }
        });
      }
      return throwError(() => error);
    })
  );
};
